<template>
    <div>
        <div class="bg-gray2" :class="showDasChargerBanner ? '' : 'pb-2'">
            <div class="container-fluid">
                <!--/* bandeau lien et icône panier */-->
                <cart-banner
                    class="py-1"
                    :cart-link="cartUrl"
                    :number-of-items="accessoriesCount"
                    :back-link="accessoryDetails.cartBannerDTO.backlinkLabel"
                    :cart-enabled="cartEnabled"
                    :is-new-selector-activated="accessoryDetails.newSelectorActivated"
                    :back-url="backUrl"
                />
                <div class="d-flex flex-column flex-md-row mt-1">
                    <!--/* nom de l'accessoire */-->
                    <h1 class="mb-0 mb-md-1 me-md-1">
                        {{ accessoryDetails.name }}
                    </h1>

                    <!--/* rating-summary */-->
                    <ob1-rating
                        v-if="displayRatings"
                        class="item-summary align-items-end align-items-xl-start mt-0 mt-xl-1 mb-1 mb-md-0"
                        :average="currentAccessory.reviewsStats.average"
                        :text="'('+currentAccessory.reviewsStats.count+' avis)'"
                        :rating-results-height="1.375"
                        link="#anchor-reviews"
                        @ob1-rating-link.prevent="scrollToPageMenuAnchor('anchor-reviews')"
                    />
                </div>
                <div class="d-flex flex-column flex-md-row bg-white">
                    <!--/* img */-->
                    <div class="d-flex flex-column info-device-width border-right-md border-gray2">
                        <div class="px-1 px-lg-2 py-1">
                            <div class="item-img">
                                <ob1-carrousel
                                    :id="'accessory-gallery'"
                                    :key="'accessory-gallery-' + currentAccessory.seoId"
                                    :images="currentAccessory.galleries"
                                    :is-product-sheet="true"
                                    :is-device-product-sheet="true"
                                    :with-zoom="true"
                                    :with-thumbnails="true"
                                />
                            </div>
                            <ob1-separateur v-if="currentAccessory.asilage" class="my-1" />
                            <asilage
                                v-if="currentAccessory.asilage"
                                :title="currentAccessory.asilage.title"
                                :banner="currentAccessory.asilage.banner"
                                :condition="currentAccessory.asilage.condition"
                                :form-url="currentAccessory.asilage.formUrl"
                                :image="currentAccessory.asilage.image"
                                :condition-label="currentAccessory.asilage.conditionLabel"
                            />
                        </div>
                    </div>

                    <!--/*Pastilles + capacités + longueurs*/-->
                    <div class="w-100">
                        <div class="px-1 px-md-2 py-1">
                            <div class="d-md-flex justify-content-md-between flex-wrap">
                                <h2
                                    v-if="showDeclinaisonName"
                                    class="fw-bold no-signature standard mb-0 mb-lg-1 me-2"
                                    v-html="subtitle"
                                />
                            </div>

                            <ob1-pastilles-de-couleurs
                                v-if="allVersions.length > 1 && numberOfColors > 0"
                                :id="'colors'"
                                ref="pastilles-de-couleurs"
                                :class="!showDeclinaisonName ? 'mt-2' : null"
                                class="mb-1"
                                :default-color="currentAccessory.color ? currentAccessory.color.code : null"
                                :colors="allColors"
                                @ob1-pastilles-de-couleurs-click="changeSelectedColor"
                            />
                            <fieldset v-if="numberOfCapacities > 0" :class="numberOfColors > 0 ? 'mb-1' : 'mt-1 mb-1'">
                                <legend class="visually-hidden">
                                    Capacités disponibles
                                </legend>
                                <ob1-chips
                                    v-for="(capacity, index) in allCapacities"
                                    :id="'capacity-' + index"
                                    :key="'capacity-' + index"
                                    :is-choice-version="true"
                                    :label="capacity.capacity"
                                    :is-disabled="!capacity.inStock"
                                    :is-checked="capacity.checked"
                                    :disable-uncheck="true"
                                    @ob1-chips-click="changeSelectedCapacity($event, capacity.capacity)"
                                />
                            </fieldset>
                            <fieldset
                                v-if="numberOfLengths > 0"
                                :class="numberOfCapacities > 0 || numberOfColors > 0 ? 'mb-1' : 'mt-1 mb-1'"
                            >
                                <legend class="visually-hidden">
                                    Longueurs disponibles
                                </legend>
                                <ob1-chips
                                    v-for="(length, index) in allLengths"
                                    :id="'length-' + index"
                                    :key="'length-' + index"
                                    :is-choice-version="true"
                                    :label="length.length"
                                    :is-disabled="!length.inStock"
                                    :is-checked="length.checked"
                                    :disable-uncheck="true"
                                    @ob1-chips-click="changeSelectedLength($event, length.length)"
                                />
                            </fieldset>

                            <template v-if="currentAccessory.accessoryPrice">
                                <ob1-separateur v-if="showDeclinaisonName" class="my-1" />
                                <div class="d-lg-flex justify-content-between">
                                    <accessory-price
                                        :accessory-price="currentAccessory.accessoryPrice"
                                        :funding-details="accessoryDetails.fundingDetails"
                                        :promo-label="promoLabel"
                                        :promo-end-date="promoEndDate"
                                        :show-guarantee-legal-mentions="accessoryDetails.showGuaranteeLegalMentions"
                                        :guarantee-legal-mentions-label="accessoryDetails.guaranteeLegalMentionsLabel"
                                    />
                                </div>
                            </template>
                            <refunds
                                v-if="currentAccessory.compactPromoValuesDto && currentAccessory.compactPromoValuesDto.odrData.length > 0"
                                class="mb-1"
                                :odrs="currentAccessory.compactPromoValuesDto.odrData"
                                :device-seo-id="currentAccessory.seoId"
                                :refund-messages="accessoryDetails.refundMessages"
                            />
                            <template v-if="currentAccessory.inStock">
                                <accessory-cta
                                    :accessory="accessoryDetails"
                                    :is-loading="isLoading"
                                    :is-add-button-enabled="isAddButtonEnabled"
                                    :cart-url="cartUrl"
                                    @accessory-cta-click="onAddAccessoryButtonClick"
                                />
                            </template>
                            <div v-else-if="currentAccessory.stockAlertDto" class="row">
                                <div class="col-12 pt-2">
                                    <ob1-message-alerte
                                        :is-simple-alerte="false"
                                        :title="currentAccessory.stockAlertDto.title"
                                        :text="currentAccessory.stockAlertDto.message"
                                        type="warning"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--/* Bandeau DAS / Batterie / Réparabilité */-->
        <div v-if="showDasChargerBanner" class="pt-1 pt-md-2 bg-gray2">
            <div class="container-fluid">
                <div class="row align-items-center pb-1">
                    <!--/* Das  */-->
                    <das
                        v-if="accessoryDetails.dasDTO"
                        class="my-1"
                        :head="accessoryDetails.dasDTO.head"
                        :trunk="accessoryDetails.dasDTO.trunk"
                        :member="accessoryDetails.dasDTO.member"
                        :prefix="accessoryDetails.dasDTO.prefix"
                        :popin-title="accessoryDetails.dasDTO.popinTitle"
                        :popin-text="accessoryDetails.dasDTO.popinDescription"
                        :is-sosh-shop="isSoshShop"
                    />

                    <!--/* Batterie(Charger) */-->
                    <div v-if="accessoryDetails.chargerIncluded" class="col-12 col-md-auto my-1 my-md-0 mx-md-2 px-md-0">
                        <div class="d-flex align-items-center">
                            <div class="icon icon-charger-availability charger-icon-size text-gray4" aria-hidden="true" />
                            <strong v-html="accessoryDetails.chargerIncludedMessage" />
                        </div>
                    </div>
                    <div v-else-if="accessoryDetails.chargerCompatibleInfosDTO" class="col-12 col-md-auto my-1 my-md-0 mx-md-2 px-md-0">
                        <ChargerInfos :charger-infos="accessoryDetails.chargerCompatibleInfosDTO" />
                    </div>
                </div>
            </div>
        </div>
        <div class="container-fluid">
            <!--/* desc */-->
            <!--/* Financing */-->
            <section v-if="shouldDisplayPXFMention">
                <div class="row mt-2 mb-1">
                    <div class="col-12">
                        <financing-mentions :financing-mentions="financingMentions" />
                    </div>
                </div>
            </section>

            <!--/* accessory desc */-->
            <ob1-separateur v-if="shouldDisplayPXFMention" class="mt-2 mb-1" />
            <div v-else class="mt-2 mb-1" />
            <p v-if="accessoryDetails.descriptionTitle" class="lead text-mw mb-0">
                <strong>{{ accessoryDetails.descriptionTitle }}</strong>
            </p>
            <p v-if="accessoryDetails.description" class="text-mw" v-html="accessoryDetails.description" />

            <!--/* Page Menu */-->
            <ob1-page-menu
                v-if="accessoryDetails.pageMenuDTO"
                id="accessory-details-page-menu"
                :tabs="accessoryDetails.pageMenuDTO.tabs"
                class="sticky bg-white mb-2"
                @ob1-page-menu-click="scrollToPageMenuAnchor"
            />

            <!--/* Push Zigzag */-->
            <section v-if="accessoryDetails.pushZigZagDTO">
                <div class="mb-xl-3" />
                <header-signature :text="accessoryDetails.essentialTitle" />"
                <push-zig-zag :zigzag="accessoryDetails.pushZigZagDTO" />
            </section>

            <!--/* Fiche Technique */-->
            <section v-if="accessoryDetails.accessoryTechnicalFeaturesDTO">
                <ob1-separateur v-if="accessoryDetails.pushZigZagDTO" class="my-2 mb-xl-3" />
                <accessory-technical-features
                    :accessory-features="accessoryDetails.accessoryTechnicalFeaturesDTO"
                />
            </section>

            <!--/* Les garanties légales */-->
            <section v-if="accessoryDetails.showGuaranteeLegalMentions">
                <ob1-separateur v-if="accessoryDetails.accessoryTechnicalFeaturesDTO" class="my-2 mb-xl-3" />
                <guarantee-legal-mentions :legal-mentions="deviceLegalMentions" />
            </section>

            <!--/* Avis clients */-->
            <template v-if="displayRatings">
                <section class="mb-2 mb-md-3">
                    <ob1-separateur v-if="accessoryDetails.showGuaranteeLegalMentions" class="my-2" />
                    <header-signature :text="accessoryDetails.reviewsMessages.title" />
                    <device-average-rating-reviews
                        :average-ratings="deviceAverageRatingReviews.averageRatings"
                        :reviews-stats-average="currentAccessory.reviewsStats.average"
                        :reviews-stats-text="'('+currentAccessory.reviewsStats.count+' avis)'"
                        :loading="deviceAverageRatingReviews.loading"
                        :reviews-messages="accessoryDetails.reviewsMessages"
                    />
                    <device-filter-and-details-reviews
                        :device-details-reviews="deviceDetailsReviews"
                        :device-filter-reviews="deviceFilterReviews"
                        :reviews-messages="accessoryDetails.reviewsMessages"
                        :ratings-filter-checkboxes="ratingsFilterCheckboxes"
                        @update-ratings-filter-handler="updateRatingsFilterHandler"
                        @more-device-reviews="getMoreDeviceReviews"
                    />
                </section>
            </template>
            <div class="mt-1">
                <ob1-liens
                    v-if="accessoryDetails.environmentalQualityDTO"
                    class="mw-100 py-0"
                    :href="accessoryDetails.environmentalQualityDTO.url"
                    :text="accessoryDetails.environmentalQualityDTO.linkLabel"
                    :with-arrow="true"
                    :is-mini="true"
                    target="'_blank'"
                />
            </div>
        </div>
        <div
            v-if="currentAccessory.accessoryPrice.price.paymentInfoLabel && currentAccessory.accessoryPrice.price.paymentInfoLabel.type === 'CB4X'"
            class="bg-gray2 mt-1 pt-1 pb-1"
        >
            <div class="container-fluid">
                <legal-mentions4x-cb
                    :label="accessoryDetails.legalMentions.cb4xMention"
                    :popin4x-cb="accessoryDetails.legalMentions.popin4xCb"
                />
            </div>
        </div>

        <section>
            <!--/* Zone Fil d'ariane */-->
            <div class="container-fluid">
                <ob1-breadcrumb :items="breadcrumbItems" />
            </div>
        </section>
        <ob1-snack-bar
            id="snackbar"
            ref="snackbar"
            icon="icon-error-severe"
            :label="errorMessage"
        />
    </div>
</template>

<script>
import AccessoryCta from "./AccessoryCta.vue";
import AccessoryPrice from "./AccessoryPrice.vue";
import AccessoryTechnicalFeatures from "./AccessoryTechnicalFeatures.vue";
import CartBanner from "../common/CartBanner.vue";
import Das from "../common/components/Das.vue";
import PushZigZag from "../../common/fragments/mobile/PushZigZag.vue";
import {ColorSorter} from "../common/mixins/ColorSorter";
import {VersionSorter} from "../common/mixins/VersionSorter";
import {CartCtrl, CartCtrlState} from "./CartCtrl";
import GuaranteeLegalMentions from "../common/components/GuaranteeLegalMentions.vue";
import {scrollToElement} from "../common/Util.js";
import HeaderSignature from "../../common/components/vue/HeaderSignature.vue";
import Asilage from "../common/components/Asilage.vue";
import FinancingMentions from "../common/components/FinancingMentions.vue";
import DeviceAverageRatingReviews from "../common/devicereviews/DeviceAverageRatingReviews.vue";
import DeviceFilterAndDetailsReviews from "../common/devicereviews/DeviceFilterAndDetailsReviews.vue";
import Refunds from "../common/components/Refunds.vue";
import ChargerInfos from "../common/ChargerInfos.vue";
import LegalMentions4xCb from "../common/components/LegalMentions4xCb.vue";

const AccessoryDetailsCtrlState = Object.freeze({
    LOADED: 1,
    LOADING: 2,
})

const accessoryDetails = JSON.parse(document.getElementById('accessory-details').dataset.accessoryDetails);

export default {
    name: "AccessoryDetails",
    components: {
        LegalMentions4xCb,
        ChargerInfos,
        Refunds,
        FinancingMentions,
        DeviceFilterAndDetailsReviews,
        DeviceAverageRatingReviews,
        Asilage,
        HeaderSignature,
        AccessoryCta,
        AccessoryPrice,
        AccessoryTechnicalFeatures,
        CartBanner,
        Das,
        PushZigZag,
        GuaranteeLegalMentions
    },
    mixins: [ColorSorter, VersionSorter],
    data() {
        return {
            accessoryDetails,
            cartUrl: accessoryDetails.cartUrl,
            shop: accessoryDetails.shop,
            orderId: accessoryDetails.orderId,
            currentAccessory: accessoryDetails.currentVersion,
            state: AccessoryDetailsCtrlState.LOADED,
            deviceLegalMentions: accessoryDetails.deviceLegalMentions,
            financingMentions: accessoryDetails.financingMentions,
            errorMessage: "",
            cartEnabled: accessoryDetails.cartUrl != null,
            accessoriesCount: accessoryDetails.cartBannerDTO.numberOfItemsInCart,
            referer: accessoryDetails.referer,
            autoScrollEnabled: true,
            carrousel: null,
            deviceDetailsReviews: {
                totalNumber: 0,
                blocSize: 10,
                reviews: [],
                offset: 0,
                loading: true
            },
            deviceFilterReviews: {
                ratingsFilter: [],
                loading: true
            },
            deviceAverageRatingReviews: {
                averageRatings: [],
                loading: true
            },
            breadcrumbItems: accessoryDetails.filArianeDTO.items,
            selectedRatingsFilter: []
        }
    },
    computed: {
        isSoshShop() {
            return this.shop === 'SOSH'
        },
        displayRatings() {
            return !!this.currentAccessory.reviewsStats;
        },
        isAddButtonEnabled() {
            if (this.currentAccessory.boutonsDTO.disabled) {
                return false;
            }
            return !this.isLoading;
        },
        isLoading() {
            return this.state === AccessoryDetailsCtrlState.LOADING;
        },
        backUrl() {
            if (this.accessoryDetails.newSelectorActivated && (!this.referer || this.referer.indexOf(this.accessoryDetails.accessorySelectorBaseUrl) === -1)) {
                return this.accessoryDetails.accessorySelectorBaseUrl
            } else {
                return ""
            }
        },
        pageMenuOffsetHeight: function () {
            if (document.querySelector('#accessory-details-page-menu')) {
                return document.querySelector('#accessory-details-page-menu').offsetHeight;
            }
            return 0;
        },
        allVersions: function () {
            // On ne doit pas afficher une pastille de couleur, avec ou sans capacité, s'il n y a qu'une seule version
            if (this.accessoryDetails.versions.length > 1) {
                if (this.numberOfLengths > 1) {
                    return this.sortVersionsByLength(this.accessoryDetails.versions)
                } else {
                    return this.accessoryDetails.versions
                }
            } else {
                return []
            }
        },
        numberOfCapacities: function () {
            return this.accessoryDetails.versions.filter(version => version.capacity !== undefined).length
        },
        numberOfLengths: function () {
            return this.accessoryDetails.versions.filter(version => version.length !== undefined).length
        },
        numberOfColors: function () {
            return this.accessoryDetails.versions.filter(version => version.color !== undefined).length
        },
        allColors: function () {
            const colors = [];
            for (const version of this.allVersions) {
                const versionColor = version.color;
                if (versionColor && !colors.find(color => color.code === versionColor.code)) {
                    colors.push(
                        {
                            colorName: versionColor.colorName,
                            code: versionColor.code,
                            isChecked: this.checkedColor && this.checkedColor.code === versionColor.code
                        }
                    );
                }
            }
            return this.sortColors(colors);
        },
        checkedCapacity: function () {
            const capacity = this.currentAccessory.capacity
            if (capacity !== null && capacity !== undefined) capacity.inStock = this.currentAccessory.inStock
            return capacity
        },
        checkedLength: function () {
            let length = this.currentAccessory.length
            if (length !== null && length !== undefined) length.inStock = this.currentAccessory.inStock
            return length
        },
        checkedColor: function () {
            let color = this.currentAccessory.color
            if (color !== null && color !== undefined) color.inStock = this.currentAccessory.inStock
            return color
        },
        allCapacities: function () {
            const capacities = [];
            // this.accessoryDetails.allCapacities accessoires triés en back
            for (const capacity of this.accessoryDetails.allCapacities) {
                let version = this.allVersions.find(version => version.capacity.label === capacity);
                if (version != null) {
                    capacities.push({
                        capacity: version.capacity.label,
                        checked: this.checkedCapacity && this.checkedCapacity.label === version.capacity.label,
                        inStock: version.inStock
                    });
                }
            }
            return capacities;
        },
        allLengths: function () {
            const lengths = [];
            for (const version of this.allVersions) {
                const versionLength = version.length;
                if (versionLength && !lengths.find(length => length.label === versionLength.label)) {
                    lengths.push(
                        {
                            length: versionLength.label,
                            checked: this.checkedLength && this.checkedLength.label === versionLength.label,
                            inStock: version.inStock
                        }
                    );
                }
            }
            return lengths;
        },
        showDeclinaisonName() {
            return this.currentAccessory.title !== this.accessoryDetails.name
        },
        shouldDisplayPXFMention() {
            return this.currentAccessory.accessoryPrice?.price.pxfAvailable;
        },
        ratingsFilterCheckboxes() {
            return this.deviceFilterReviews.ratingsFilter.map(rating => {
                return {
                    label: rating.label,
                    isChecked: this.selectedRatingsFilter.includes(rating.id),
                    count: rating.count,
                    id: rating.id,
                }
            })
        },
        promoEndDate() {
            if (this.currentAccessory.promoMessagesDTO.promo && this.currentAccessory.promoMessagesDTO.promo.endDate) {
                return this.currentAccessory.promoMessagesDTO.promo.endDate
            }
            return undefined
        },
        promoLabel() {
          if (this.currentAccessory.promoMessagesDTO.promo && this.currentAccessory.promoMessagesDTO.promo.promoLabel) {
                return this.currentAccessory.promoMessagesDTO.promo.promoLabel
            }
            return undefined
        },
        subtitle() {
            let subtitle = `${this.currentAccessory.title}`;
            if (this.currentAccessory.accessoryPrice.promoBanner) {
                return `${this.currentAccessory.accessoryPrice.promoBanner} ${subtitle}`;
            } else {
                return `${subtitle}`;
            }
        },
        showDasChargerBanner() {
            return !!(this.accessoryDetails.dasDTO || this.accessoryDetails.chargerIncluded || this.accessoryDetails.chargerCompatibleInfosDTO)
        }
    },
    created: function () {
        // Scrute le déplacement dans la page, pour mettre à jour la section consultée dans la barre de menu sticky
        document.addEventListener('scroll', () => {
            if (this.accessoryDetails.pageMenuDTO && this.autoScrollEnabled) {
                updatePageMenu('accessory-details-page-menu', this.accessoryDetails.pageMenuDTO.tabs.map(tab => "#" + tab.id));
            }
        });
    },
    mounted() {
        this.cartController = new CartCtrl({
            orderId: this.accessoryDetails.orderId,
            isLegacyCart: this.accessoryDetails.legacyCart,
            initCartAccessoryApiUrl: this.accessoryDetails.initCartAccessoryApiUrl,
            accessoriesCount: this.accessoriesCount,
            cartUrl: this.cartUrl
        });
        this.currentAccessory.radioButtonDTO.checked = true;
        this.cartEnabled = this.cartController.state !== CartCtrlState.UNINITIALIZED;
        Array.from(document.querySelectorAll(".ob1-carrousel-product-sheet")).forEach((carrousel) => {
                this.carrousel = new ob1.Carrousel(carrousel, false);
            }
        );
        this.loadDeviceReviews();
    },
    methods: {
        changeSelectedColor(clickedColor) {
            const currentAccessoryLength = this.currentAccessory.length ? this.currentAccessory.length.label : undefined
            const currentAccessoryCapacity = this.currentAccessory.capacity ? this.currentAccessory.capacity.label : undefined
            if (currentAccessoryLength && currentAccessoryCapacity) {
                this.currentAccessory = this.findVersionByCapacityLengthAndColor(currentAccessoryCapacity, currentAccessoryLength, clickedColor?.code)
            } else if (currentAccessoryLength) {
                this.currentAccessory = this.findVersionByLengthAndColor(currentAccessoryLength, clickedColor?.code, true, false)
            } else if (currentAccessoryCapacity) {
                this.currentAccessory = this.findVersionByCapacityAndColor(currentAccessoryCapacity, clickedColor?.code, false, true)
            } else {
                this.currentAccessory = this.findVersionByColor(clickedColor?.code)
            }

            this.updateInfos();
        },
        changeSelectedCapacity(event, clickedCapacity) {
            const currentAccessoryColor = this.currentAccessory.color ? this.currentAccessory.color.code : undefined
            const currentAccessoryLength = this.currentAccessory.length ? this.currentAccessory.length.label : undefined
            if (currentAccessoryColor && currentAccessoryLength) {
                this.currentAccessory = this.findVersionByCapacityLengthAndColor(clickedCapacity, currentAccessoryLength, currentAccessoryColor)
            } else if (currentAccessoryColor) {
                this.currentAccessory = this.findVersionByCapacityAndColor(clickedCapacity, currentAccessoryColor, true, false)
            } else if (currentAccessoryLength) {
                this.currentAccessory = this.findVersionByCapacityAndLength(clickedCapacity, currentAccessoryLength, true, false)
            } else {
                this.currentAccessory = this.findVersionByCapacity(clickedCapacity)
            }

            this.updateInfos();
        },
        changeSelectedLength(event, clickedLength) {
            const currentAccessoryColor = this.currentAccessory.color ? this.currentAccessory.color.code : undefined
            const currentAccessoryCapacity = this.currentAccessory.capacity ? this.currentAccessory.capacity.label : undefined
            if (currentAccessoryColor && currentAccessoryCapacity) {
                this.currentAccessory = this.findVersionByCapacityLengthAndColor(currentAccessoryCapacity, clickedLength, currentAccessoryColor)
            } else if (currentAccessoryColor) {
                this.currentAccessory = this.findVersionByLengthAndColor(clickedLength, currentAccessoryColor, false, true)
            } else if (currentAccessoryCapacity) {
                this.currentAccessory = this.findVersionByCapacityAndLength(currentAccessoryCapacity, clickedLength, false, true)
            } else {
                this.currentAccessory = this.findVersionByLength(clickedLength)
            }

            this.updateInfos();
        },
        updateInfos() {
            // On met à jour l'url
            this.updateUrl();

            // On met à jour le fil d'ariane
            this.breadcrumbHandler();
        },
        findVersionByCapacity(capacity) {
            return this.allVersions.find(version =>
                version.capacity?.label === capacity
            )
        },
        findVersionByLength(length) {
            return this.allVersions.find(version =>
                version.length?.label === length
            )
        },
        findVersionByColor(colorCode) {
            return this.allVersions.find(version =>
                version.color?.code === colorCode
            )
        },
        findVersionByCapacityAndLength(capacity, length, findByCapacity, findByLength) {
            let versionToReturn = null
            versionToReturn = this.allVersions.find(version =>
                version.capacity?.label === capacity && version.length?.label === length
            )
            if (!versionToReturn && findByCapacity) {
                versionToReturn = this.findVersionByCapacity(capacity)
            } else if (!versionToReturn && findByLength) {
                versionToReturn = this.findVersionByLength(length)
            }
            return versionToReturn
        },
        findVersionByCapacityAndColor(capacity, colorCode, findByCapacity, findByColor) {
            let versionToReturn = null
            versionToReturn = this.allVersions.find(version =>
                version.capacity?.label === capacity && version.color?.code === colorCode
            )
            if (!versionToReturn && findByCapacity) {
                const pastilles = this.$refs["pastilles-de-couleurs"]
                versionToReturn = this.findVersionByCapacity(capacity)
                pastilles.currentColorCode = versionToReturn.color?.code
            } else if (!versionToReturn && findByColor) {
                versionToReturn = this.findVersionByColor(colorCode)
            }
            return versionToReturn
        },
        findVersionByLengthAndColor(length, colorCode, findByColor, findByLength) {
            //on cherche le duo couleur / nouvelle longueur ou longueur / nouvelle couleur.
            //si on ne trouve pas ce duo, et qu'on a cliqué sur une couleur, on va renvoyer la première version trouvée avec la nouvelle couleur.
            //si on a cliqué sur une longueur, on va renvoyer la première version trouvée avec cette longueur.
            let versionToReturn = null
            versionToReturn = this.allVersions.find(version =>
                version.length?.label === length && version.color?.code === colorCode
            )
            if (!versionToReturn && findByLength) {
                const pastilles = this.$refs["pastilles-de-couleurs"]
                versionToReturn = this.findVersionByLength(length)
                pastilles.currentColorCode = versionToReturn.color?.code
            } else if (!versionToReturn && findByColor) {
                versionToReturn = this.findVersionByColor(colorCode)
            }
            return versionToReturn
        },
        findVersionByCapacityLengthAndColor(capacity, length, colorCode, findByCapacity, findByLength, findByColor) {
            //si on ne trouve pas le trio capacité/longueur/couleur et qu'on a cliqué sur une capacité, on cherche le duo capacité/longueur ou capacité/couleur. Si on les trouve pas on cherche la première version de la capacité sélectionnée
            //si pas trio capacité/longueur/couleur et clic sur couleur, on cherche duo couleur/capacité ou couleur/longueur. Si on ne les trouve pas, on cherche la première version avec la couleur sélectionnée.
            //si pas trio capacité/longueur/couleur et clic sur longueur, on cherche duo longueur/capacité ou longueur/couleur. Si on ne les trouve pas, on cherche la première version avec la longueur sélectionnée
            let versionToReturn = null
            versionToReturn = this.allVersions.find(version =>
                version.length?.label === length && version.color?.code === colorCode && version.capacity?.label === capacity
            )
            if (!versionToReturn && findByCapacity) {
                versionToReturn = this.findVersionByCapacityAndLength(capacity, length, true, false)
                if (!versionToReturn) {
                    versionToReturn = this.findVersionByCapacityAndColor(capacity, length, true, false)
                }
            } else if (!versionToReturn && findByLength) {
                versionToReturn = this.findVersionByLengthAndColor(length, colorCode, false, true)
                if (!versionToReturn) {
                    versionToReturn = this.findVersionByCapacityAndLength(capacity, length, false, true)
                }
            } else if (!versionToReturn && findByColor) {
                versionToReturn = this.findVersionByLengthAndColor(length, colorCode, true, false)
                if (!versionToReturn) {
                    versionToReturn = this.findVersionByCapacityAndColor(capacity, colorCode, false, true)
                }
            }
            return versionToReturn
        },
        updateUrl() {
            //On met à jour l'url avec le seoId de la déclinaison sélectionnée
            const slug = window.location.href.substring(
                window.location.href.lastIndexOf("/") + 1
            )
            const parameterIndex = window.location.href.lastIndexOf("?")
            const parameters = parameterIndex === -1 ? "" : window.location.href.substring(parameterIndex)
            const fullUrlWithoutSlug = window.location.href.substring(
                0, window.location.href.lastIndexOf(slug)
            )
            const updatedUrl = fullUrlWithoutSlug + this.currentAccessory.seoId + parameters
            window.history.replaceState({}, null, updatedUrl)
        },
        breadcrumbHandler() {
            const breadcrumbLength = this.breadcrumbItems.length
            this.breadcrumbItems.splice(breadcrumbLength - 1, 1, {name: this.currentAccessory.title})
        },
        enableAddAccessoryButton(enable) {
            const elt = this.$refs["add-accessory-cta"];
            if (elt) {
                elt.isDisabledData = !enable;
            }
        },
        onAddAccessoryButtonClick() {
            Tms.link({
                category: this.accessoryDetails.addToCartDTO.tmsDTO.tagZone,
                action: this.accessoryDetails.addToCartDTO.tmsDTO.tagName,
                label: this.accessoryDetails.addToCartDTO.tmsDTO.tagTarget
            });
            this.state = AccessoryDetailsCtrlState.LOADING;

            // FIXME: la property isDisabled sur le ob1-boutons n'est pas réactive
            //  donc pour l'instant on force en dur la désactivation du bouton d'ajout d'accessoire.
            this.enableAddAccessoryButton(false);
            this.cartController.addAccessory(this.currentAccessory.seoId, this.accessoryDetails.shop)
                .then((cart) => {
                    this.accessoriesCount = this.cartController.accessoriesCount;
                    this.orderId = this.cartController.orderId;
                    this.cartUrl = this.cartController.cartUrl;
                    this.cartEnabled = true;
                    this.state = AccessoryDetailsCtrlState.LOADED;
                    this.enableAddAccessoryButton(true);
                    this.showAddedModal();
                })
                .catch(() => {
                    this.cartEnabled = this.cartController.state !== CartCtrlState.UNINITIALIZED;
                    if (this.cartController.state === CartCtrlState.ERROR_MAX_ARTICLE) {
                        this.errorMessage = this.accessoryDetails.addToCartMaxArticleCountErrorMessage;
                    } else if (this.cartController.state === CartCtrlState.ERROR_ALREADY_SAME_ARTICLE) {
                        this.errorMessage = this.accessoryDetails.addToCartAlreadySameArticleInCartErrorMessage;
                    } else if (this.cartController.state === CartCtrlState.ERROR_OUT_OF_STOCK) {
                        this.errorMessage = this.accessoryDetails.addToCartOutOfStockErrorMessage;
                    } else {
                        this.errorMessage = this.accessoryDetails.addToCartGenericErrorMessage;
                    }
                    this.state = AccessoryDetailsCtrlState.LOADED;
                    this.enableAddAccessoryButton(true);
                    this.showErrorSnackBar();
                });
        },
        showAddedModal() {
            $('#add-to-cart').modal('show');
        },
        showErrorSnackBar() {
            const snackbar = this.$refs.snackbar;
            if (snackbar) {
                snackbar.show();
            }
        },
        scrollToPageMenuAnchor: function (id) {
            const element = document.getElementById(id);
            if (element) {
                // on ajoute 2px pour trigger l'événement de changement d'onglet et que le bon soit indiqué comme actif
                scrollToElement(element, {dx: 0, dy: -this.pageMenuOffsetHeight + 2});
            }
        },
        getMoreDeviceReviews: function () {
            if (this.displayRatings) {
                this.deviceDetailsReviews.offset += this.deviceDetailsReviews.blocSize;
                this.fetchDeviceReviews().then(result => {
                    this.deviceDetailsReviews.reviews = this.deviceDetailsReviews.reviews.concat(result.reviews);
                });
            }
        },
        updateDeviceDetailsReviews: function () {
            if (this.displayRatings) {
                this.deviceDetailsReviews.loading = true;
                this.fetchDeviceReviews().then(result => {
                    this.deviceDetailsReviews.totalNumber = result.totalNumber;
                    this.deviceDetailsReviews.blocSize = result.blocSize;
                    this.deviceDetailsReviews.reviews = result.reviews;
                    this.deviceDetailsReviews.loading = false;
                });
            }
        },
        loadDeviceReviews: function () {
            if (this.displayRatings) {
                this.selectedRatingsFilter = [];
                this.deviceDetailsReviews.loading = true;
                this.deviceFilterReviews.loading = true;
                this.deviceAverageRatingReviews.loading = true;
                this.fetchDeviceReviews().then(result => {
                    this.deviceDetailsReviews.totalNumber = result.totalNumber;
                    this.deviceDetailsReviews.blocSize = result.blocSize;
                    this.deviceDetailsReviews.reviews = result.reviews;
                    this.deviceAverageRatingReviews.averageRatings = result.averageRatings;
                    this.deviceFilterReviews.ratingsFilter = result.ratingsFilter;
                    this.deviceDetailsReviews.loading = false;
                    this.deviceFilterReviews.loading = false;
                    this.deviceAverageRatingReviews.loading = false;
                });
            }
        },
        fetchDeviceReviews: function () {
            let url = "/api/mobile/accessory-reviews"
            return new Promise((resolve) => {
                $.ajax({
                    method: "GET",
                    data: {
                        "shop": this.shop,
                        "eans": this.accessoryDetails.versions.map(it => it.ean),
                        "offset": this.deviceDetailsReviews.offset,
                        "ratings-input-filter": this.selectedRatingsFilter.toString(),
                    },
                    xhrFields: {
                        withCredentials: true
                    },
                    traditional: true, // pour éviter que jquery rename le paramètre eans -> eans[]
                    crossDomain: true,
                    url: url,
                    contentType: "application/json",
                    dataType: "json"
                }).done(result => {
                    resolve(result)
                }).fail((error) => {
                    resolve(error.responseJSON)
                });
            });
        },
        updateRatingsFilterHandler(selectedRatingsFilter) {
            this.selectedRatingsFilter = selectedRatingsFilter;
            this.deviceDetailsReviews.offset = 0;
            this.updateDeviceDetailsReviews()
        }
    }
}
</script>

<style scoped lang="scss">
@import 'ob1_common', 'ob1_variables';

.item-summary {
    height:  px-to-rem(32)
}

.sticky {
    position: sticky;
    top: 0;
    z-index: 3;
}

#rvs {
    min-height: px-to-rem(350);
}

.border-right-md {
    @media (min-width: #{map-get($grid-breakpoints, md)}) {
        border-right: px-to-rem(30) solid;
    }
}

.info-device-width {
    width: 100%;
    @media (min-width: #{map-get($grid-breakpoints, md)}) {
        width: 46%;

    }
}

</style>

<style lang="scss">
/* Permet au navigateur de réserver de l'espace même si les images sont lazy ! Et corrige donc le scrollTo() */
.ob1-push-zigzag .card-header {
    width: 100%;
    aspect-ratio: 4 / 3;
}
</style>
